import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  MoreVert as MoreIcon,
  CheckCircle as PaidIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelledIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  CreditCard as CardIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  TrendingUp as SalesIcon,
  Assessment as ReportIcon,
} from "@mui/icons-material";
import ExportButton from "../../../components/ExportButton/ExportButton";

const InvoiceList = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Sample invoices data
  const [invoices, setInvoices] = useState([
    {
      id: 1,
      invoiceNo: "INV1001",
      date: "2024-01-20",
      customerName: "Rajesh Kumar",
      customerCode: "CUST001",
      items: 3,
      subtotal: 89997,
      discount: 8999.70,
      tax: 14039.62,
      grandTotal: 95036.92,
      paidAmount: 95037,
      paymentMethod: "UPI",
      status: "Paid",
      cashier: "Admin User",
    },
    {
      id: 2,
      invoiceNo: "INV1002",
      date: "2024-01-20",
      customerName: "Priya Sharma",
      customerCode: "CUST002",
      items: 2,
      subtotal: 84998,
      discount: 0,
      tax: 15299.64,
      grandTotal: 100297.64,
      paidAmount: 100300,
      paymentMethod: "Card",
      status: "Paid",
      cashier: "Admin User",
    },
    {
      id: 3,
      invoiceNo: "INV1003",
      date: "2024-01-20",
      customerName: "Walk-in Customer",
      customerCode: "CUST003",
      items: 1,
      subtotal: 3499,
      discount: 0,
      tax: 419.88,
      grandTotal: 3918.88,
      paidAmount: 4000,
      paymentMethod: "Cash",
      status: "Paid",
      cashier: "Admin User",
    },
    {
      id: 4,
      invoiceNo: "INV1004",
      date: "2024-01-19",
      customerName: "Amit Patel",
      customerCode: "CUST004",
      items: 5,
      subtotal: 125495,
      discount: 12549.50,
      tax: 20329.29,
      grandTotal: 133274.79,
      paidAmount: 130000,
      paymentMethod: "Net Banking",
      status: "Pending",
      cashier: "Admin User",
    },
    {
      id: 5,
      invoiceNo: "INV1005",
      date: "2024-01-19",
      customerName: "Sneha Desai",
      customerCode: "CUST005",
      items: 2,
      subtotal: 7498,
      discount: 749.80,
      tax: 1214.87,
      grandTotal: 7963.07,
      paidAmount: 8000,
      paymentMethod: "Cash",
      status: "Paid",
      cashier: "Admin User",
    },
    {
      id: 6,
      invoiceNo: "INV1006",
      date: "2024-01-18",
      customerName: "Vikram Singh",
      customerCode: "CUST006",
      items: 1,
      subtotal: 29999,
      discount: 3000,
      tax: 4859.82,
      grandTotal: 31858.82,
      paidAmount: 0,
      paymentMethod: "Card",
      status: "Cancelled",
      cashier: "Admin User",
    },
    {
      id: 7,
      invoiceNo: "INV1007",
      date: "2024-01-18",
      customerName: "Walk-in Customer",
      customerCode: "CUST003",
      items: 4,
      subtotal: 16996,
      discount: 0,
      tax: 2039.52,
      grandTotal: 19035.52,
      paidAmount: 20000,
      paymentMethod: "Cash",
      status: "Paid",
      cashier: "Admin User",
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);

  // Helper functions
  const getFilteredInvoices = () => {
    let filtered = invoices;

    if (searchQuery) {
      filtered = filtered.filter((invoice) =>
        Object.values(invoice).some((value) =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((inv) => inv.status === statusFilter);
    }

    if (paymentFilter !== "all") {
      filtered = filtered.filter((inv) => inv.paymentMethod === paymentFilter);
    }

    if (dateFrom) {
      filtered = filtered.filter((inv) => inv.date >= dateFrom);
    }

    if (dateTo) {
      filtered = filtered.filter((inv) => inv.date <= dateTo);
    }

    if (activeTab === 1) {
      filtered = filtered.filter((inv) => inv.status === "Paid");
    } else if (activeTab === 2) {
      filtered = filtered.filter((inv) => inv.status === "Pending");
    } else if (activeTab === 3) {
      filtered = filtered.filter((inv) => inv.status === "Cancelled");
    }

    return filtered;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'Pending': return { bg: '#fff3e0', color: '#e65100' };
      case 'Cancelled': return { bg: '#ffebee', color: '#d32f2f' };
      default: return { bg: '#f5f5f5', color: '#666' };
    }
  };

  const getPaymentIcon = (method) => {
    switch (method) {
      case 'Cash': return <MoneyIcon />;
      case 'Card': return <CardIcon />;
      case 'UPI': return <MoneyIcon />;
      case 'Net Banking': return <MoneyIcon />;
      default: return <MoneyIcon />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Handlers
  const handleMenuOpen = (event, invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const handleView = (invoice) => {
    setViewingInvoice(invoice);
    setOpenViewModal(true);
    handleMenuClose();
  };

  const handlePrint = (invoice) => {
    setSuccessMessage(`Printing invoice ${invoice.invoiceNo}...`);
    setTimeout(() => setSuccessMessage(""), 3000);
    handleMenuClose();
  };

  const handleCancel = (invoice) => {
    if (window.confirm(`Are you sure you want to cancel invoice ${invoice.invoiceNo}?`)) {
      setInvoices(invoices.map(inv => 
        inv.id === invoice.id ? { ...inv, status: 'Cancelled', paidAmount: 0 } : inv
      ));
      setSuccessMessage(`Invoice ${invoice.invoiceNo} cancelled successfully!`);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
    handleMenuClose();
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    setSuccessMessage(`Search completed. Found ${getFilteredInvoices().length} invoices.`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPaymentFilter("all");
    setDateFrom("");
    setDateTo("");
    setPage(0);
    setSuccessMessage("");
  };

  const handleExport = () => {
    setSuccessMessage("Invoice list exported successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const filteredInvoices = getFilteredInvoices();

  // Calculate statistics
  const totalInvoices = invoices.length;
  const paidInvoices = invoices.filter(inv => inv.status === "Paid").length;
  const pendingInvoices = invoices.filter(inv => inv.status === "Pending").length;
  const totalRevenue = invoices.filter(inv => inv.status === "Paid").reduce((sum, inv) => sum + inv.grandTotal, 0);
  const pendingAmount = invoices.filter(inv => inv.status === "Pending").reduce((sum, inv) => sum + (inv.grandTotal - inv.paidAmount), 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ReceiptIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Invoice List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage all sales invoices • {filteredInvoices.length} invoices
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ExportButton
            onExportPdf={() => {
              setSuccessMessage("Exporting Invoice List as PDF...");
              setTimeout(() => setSuccessMessage(""), 3000);
            }}
            onExportCsv={() => {
              setSuccessMessage("Exporting Invoice List as CSV...");
              setTimeout(() => setSuccessMessage(""), 3000);
            }}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          />
          <Button
            variant="outlined"
            startIcon={<ReportIcon />}
            sx={{ borderRadius: 2 }}
          >
            Generate Report
          </Button>
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Invoices</Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {totalInvoices}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Paid</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {paidInvoices}
                  </Typography>
                </Box>
                <PaidIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Pending</Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {pendingInvoices}
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #9c27b0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
                  <Typography variant="h6" fontWeight="bold" color="secondary.main">
                    {formatCurrency(totalRevenue)}
                  </Typography>
                </Box>
                <SalesIcon sx={{ fontSize: 48, color: 'secondary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #d32f2f' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Pending Amount</Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.main">
                    {formatCurrency(pendingAmount)}
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label={<Badge badgeContent={totalInvoices} color="primary">All Invoices</Badge>} 
            icon={<ReceiptIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label={<Badge badgeContent={paidInvoices} color="success">Paid</Badge>} 
            icon={<PaidIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label={<Badge badgeContent={pendingInvoices} color="warning">Pending</Badge>} 
            icon={<PendingIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label={<Badge badgeContent={invoices.filter(i => i.status === "Cancelled").length} color="error">Cancelled</Badge>} 
            icon={<CancelledIcon />} 
            iconPosition="start" 
          />
        </Tabs>
      </Paper>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Invoices..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                label="Payment Method"
              >
                <MenuItem value="all">All Methods</MenuItem>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Card">Card</MenuItem>
                <MenuItem value="UPI">UPI</MenuItem>
                <MenuItem value="Net Banking">Net Banking</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleSearch} startIcon={<SearchIcon />} sx={{ flex: 1 }}>
                Search
              </Button>
              <Button variant="outlined" onClick={handleReset} startIcon={<RefreshIcon />} sx={{ flex: 1 }}>
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Invoice No</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Items</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Payment</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices.length > 0 ? (
                filteredInvoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((invoice) => {
                  const statusStyle = getStatusColor(invoice.status);
                  const balance = invoice.grandTotal - invoice.paidAmount;
                  return (
                    <TableRow key={invoice.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="700" sx={{ fontFamily: 'monospace' }}>
                          {invoice.invoiceNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{formatDate(invoice.date)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.875rem' }}>
                            {invoice.customerName.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="600">{invoice.customerName}</Typography>
                            <Typography variant="caption" color="text.secondary">{invoice.customerCode}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={`${invoice.items} items`} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="700" color="primary">
                            {formatCurrency(invoice.grandTotal)}
                          </Typography>
                          {invoice.discount > 0 && (
                            <Typography variant="caption" color="success.main">
                              Saved: {formatCurrency(invoice.discount)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Chip
                            icon={getPaymentIcon(invoice.paymentMethod)}
                            label={invoice.paymentMethod}
                            size="small"
                            sx={{ mb: 0.5 }}
                          />
                          {balance > 0 && invoice.status === "Pending" && (
                            <Typography variant="caption" color="error.main" display="block">
                              Due: {formatCurrency(balance)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.status}
                          size="small"
                          sx={{
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="More Actions">
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleMenuOpen(e, invoice)}
                            sx={{ color: 'primary.main' }}
                          >
                            <MoreIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">No invoices found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredInvoices.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleView(selectedInvoice)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handlePrint(selectedInvoice)}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print Invoice</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        <Divider />
        {selectedInvoice?.status === 'Pending' && (
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon>
              <EditIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Edit Invoice</ListItemText>
          </MenuItem>
        )}
        {selectedInvoice?.status !== 'Cancelled' && (
          <MenuItem onClick={() => handleCancel(selectedInvoice)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Cancel Invoice</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* View Invoice Modal */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
        }}>
          <Typography variant="h6" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon /> Invoice Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {viewingInvoice && (
            <Grid container spacing={3}>
              {/* Invoice Header */}
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', mb: 2, pb: 2, borderBottom: '2px solid #000' }}>
                  <Typography variant="h5" fontWeight="bold">YOUR STORE NAME</Typography>
                  <Typography variant="body2">123 Store Address, City - 400001</Typography>
                  <Typography variant="body2">Phone: +91 12345 67890 | Email: store@email.com</Typography>
                </Box>
              </Grid>

              {/* Invoice Info */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Invoice No:</Typography>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                        {viewingInvoice.invoiceNo}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">Date:</Typography>
                      <Typography variant="body1" fontWeight="600">
                        {formatDate(viewingInvoice.date)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Customer:</Typography>
                      <Typography variant="body1" fontWeight="600">{viewingInvoice.customerName}</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">Status:</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip 
                          label={viewingInvoice.status} 
                          sx={{ 
                            bgcolor: getStatusColor(viewingInvoice.status).bg,
                            color: getStatusColor(viewingInvoice.status).color
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Summary */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Summary</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(83, 109, 254, 0.05)', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Items</Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {viewingInvoice.items}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.05)', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                  <Typography variant="h6" fontWeight="bold" color="secondary">
                    {formatCurrency(viewingInvoice.subtotal)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Tax (GST)</Typography>
                  <Typography variant="h6" fontWeight="bold" color="warning.main">
                    {formatCurrency(viewingInvoice.tax)}
                  </Typography>
                </Paper>
              </Grid>

              {/* Grand Total */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: '#f5f5f5', textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">Grand Total</Typography>
                  <Typography variant="h3" fontWeight="bold" color="primary">
                    {formatCurrency(viewingInvoice.grandTotal)}
                  </Typography>
                </Paper>
              </Grid>

              {/* Payment Details */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Payment Details</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      icon={getPaymentIcon(viewingInvoice.paymentMethod)}
                      label={viewingInvoice.paymentMethod} 
                      color="primary"
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="caption" color="text.secondary">Amount Paid</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main" sx={{ mt: 1 }}>
                    {formatCurrency(viewingInvoice.paidAmount)}
                  </Typography>
                </Paper>
              </Grid>
              {viewingInvoice.status === "Pending" && viewingInvoice.grandTotal > viewingInvoice.paidAmount && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Balance Due: {formatCurrency(viewingInvoice.grandTotal - viewingInvoice.paidAmount)}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenViewModal(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Download PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceList;

